import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useLocation, useNavigate } from "react-router-dom";

const fieldClass =
  "w-full min-h-12 px-3.5 border border-pleiades-border-strong rounded-sm bg-white text-[16px] sm:text-[14px] focus:outline-none focus:border-pleiades-blue focus:ring-1 focus:ring-pleiades-blue";

export default function LoginPage() {
  const { login, registerClient, loginWithGoogle } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const requestedPath =
    typeof location.state?.from === "string" &&
    location.state.from.startsWith("/") &&
    !location.state.from.startsWith("//")
      ? location.state.from
      : null;
  const postLoginPath = requestedPath?.startsWith("/danisan-paneli")
    ? "/danisan-paneli"
    : requestedPath;
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [googleConfig, setGoogleConfig] = useState(null);
  const googleButton = useRef(null);

  useEffect(() => {
    api
      .get("/auth/config")
      .then(({ data }) => setGoogleConfig(data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!googleConfig?.google_enabled || !googleButton.current) return;
    const render = () => {
      if (!window.google?.accounts?.id || !googleButton.current) return;
      window.google.accounts.id.initialize({
        client_id: googleConfig.google_client_id,
        callback: async ({ credential }) => {
          setBusy(true);
          const res = await loginWithGoogle(credential);
          setBusy(false);
          if (!res.ok) {
            setError(res.error);
            toast.error(res.error);
          } else if (postLoginPath) {
            navigate(postLoginPath, { replace: true });
          }
        },
      });
      googleButton.current.innerHTML = "";
      const googleButtonWidth = Math.floor(
        Math.min(400, googleButton.current.getBoundingClientRect().width || 360),
      );
      window.google.accounts.id.renderButton(googleButton.current, {
        theme: "filled_blue",
        size: "large",
        shape: "rectangular",
        logo_alignment: "left",
        width: googleButtonWidth,
        text: "continue_with",
      });
    };
    if (window.google?.accounts?.id) return render();
    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.onload = render;
    document.head.appendChild(script);
    return () => script.remove();
  }, [googleConfig, loginWithGoogle, navigate, postLoginPath]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    let res;
    if (mode === "register") {
      res = await registerClient({
        email,
        password,
        name,
      });
    } else {
      res = await login(email, password);
    }
    setBusy(false);
    if (!res.ok) {
      setError(res.error);
      toast.error(res.error);
    } else {
      toast.success(mode === "register" ? "Kaydınız oluşturuldu" : "Hoş geldiniz");
      if (postLoginPath) navigate(postLoginPath, { replace: true });
    }
  };

  const chooseMode = (next) => {
    setMode(next);
    setError("");
    setPassword("");
  };

  return (
    <div className="auth-page">
      <header className="auth-header">
        <div className="auth-header-inner">
          <span className="auth-brand-mark" aria-hidden="true">
            P
          </span>
          <span>
            <strong>Pleiades Rehberlik</strong>
            <small>Güvenli danışan alanı</small>
          </span>
        </div>
      </header>

      <main className="auth-main">
        <section className="auth-panel" aria-label="Giriş ve kayıt">
          <div className="auth-google">
            {googleConfig?.google_enabled ? (
              <div
                ref={googleButton}
                className="min-h-12 flex w-full max-w-full justify-center overflow-hidden"
              />
            ) : (
              <div className="text-center text-[11px] text-pleiades-muted border border-pleiades-border py-3">
                Google ile giriş yakında aktif olacak.
              </div>
            )}
            <div className="flex items-center gap-3 mt-4">
              <span className="h-px bg-pleiades-border flex-1" />
              <span className="text-[11px] text-pleiades-muted">veya e-posta ile</span>
              <span className="h-px bg-pleiades-border flex-1" />
            </div>
          </div>

          <div className="auth-mode-switch" aria-label="Hesap işlemi">
            {[
              ["login", "Giriş yap"],
              ["register", "Kayıt ol"],
            ].map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => chooseMode(key)}
                aria-pressed={mode === key}
                className={`min-h-11 text-[13px] rounded-sm transition-colors ${mode === key ? "bg-pleiades-ink text-white" : "text-pleiades-slate hover:bg-pleiades-ivory"}`}
              >
                {label}
              </button>
            ))}
          </div>

          <form onSubmit={onSubmit} className="auth-form">
            {mode === "register" && (
              <div>
                <label htmlFor="auth-name" className="block text-[13px] font-medium text-pleiades-slate mb-1.5">
                  Ad soyad
                </label>
                <input
                  id="auth-name"
                  className={fieldClass}
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoComplete="name"
                />
              </div>
            )}
            <div>
              <label
                htmlFor="auth-email"
                className="block text-[13px] font-medium text-pleiades-slate mb-1.5"
              >
                E-posta
              </label>
              <input
                id="auth-email"
                type="email"
                className={fieldClass}
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
              />
            </div>
            <div>
              <label
                htmlFor="auth-password"
                className="block text-[13px] font-medium text-pleiades-slate mb-1.5"
              >
                Şifre
              </label>
              <input
                id="auth-password"
                type="password"
                minLength={mode === "register" ? 8 : undefined}
                className={fieldClass}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete={mode === "register" ? "new-password" : "current-password"}
              />
              {mode === "register" && (
                <div className="text-[12px] text-pleiades-muted mt-1.5">En az 8 karakter kullanın.</div>
              )}
            </div>
            {error && (
              <div
                role="alert"
                className="text-[13px] text-red-800 bg-red-50 border border-red-300 px-3 py-2.5 rounded-sm"
              >
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={busy}
              className="w-full min-h-12 bg-pleiades-blue hover:bg-pleiades-blue-hover text-white text-[14px] font-semibold rounded-sm transition-colors disabled:bg-pleiades-sidebar disabled:text-pleiades-slate disabled:cursor-not-allowed"
            >
              {busy ? "İşleniyor…" : mode === "register" ? "Danışan hesabı oluştur" : "Giriş yap"}
            </button>
          </form>

          <p className="mt-4 text-center text-[11px] leading-relaxed text-pleiades-muted">
            Kişisel verilerin kullanımını{" "}
            <a
              href="/kvkk-aydinlatma-metni"
              target="_blank"
              rel="noreferrer"
              className="font-medium text-pleiades-blue underline underline-offset-2"
            >
              Aydınlatma Metni
            </a>
            ’nden inceleyebilirsiniz.
          </p>

        </section>
      </main>
    </div>
  );
}
